import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const LOGO_10_ANNI = "/assets/logo-10-anni.png";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5dd9b3dc-6b25-42bc-8400-ec9eef198dee/images/58fb0bb5532f7fc093f9dc85e5061b869831d80a8d6e1a29ed6dced2c154c34c.png";

export default function Landing() {
    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="landing-page">
            {/* HEADER */}
            <nav className="fixed top-0 inset-x-0 z-50 border-b border-zinc-900/70 bg-[#050505]/80 backdrop-blur-md">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
                        <div className="h-7 w-7 rounded-full bg-tropical grain" />
                        <span className="font-display text-xl tracking-tight">
                            StartupItalia<span className="text-gradient-tropical">.</span>
                        </span>
                    </Link>
                    <Link to="/login" className="btn-rebel" data-testid="nav-login-btn">
                        Accedi <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>
            </nav>

            {/* HERO */}
            <section className="relative pt-40 pb-24 lg:pt-48 lg:pb-32 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-25 mix-blend-screen pointer-events-none"
                    style={{
                        backgroundImage: `url(${HERO_BG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/60 to-[#050505] pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <span className="label-tag fade-up" data-testid="hero-label">Investor Relations</span>
                        <h1
                            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9] tracking-tighter fade-up delay-1"
                            data-testid="hero-title"
                        >
                            Tutte le <span className="font-serif-italic text-gradient-tropical">informazioni</span><br />
                            a colpo d&apos;occhio.
                        </h1>
                        <p
                            className="mt-8 max-w-xl text-zinc-400 text-lg leading-relaxed fade-up delay-2"
                            data-testid="hero-subtitle"
                        >
                            Informiamo i nostri investitori regolarmente e con la massima trasparenza. In queste
                            pagine troverai le informazioni societarie e finanziarie più importanti su StartupItalia.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4 fade-up delay-3">
                            <Link to="/login" className="btn-rebel" data-testid="hero-cta-login">
                                Entra in area soci
                            </Link>
                            <Link to="/non-riesco-accedere" className="btn-ghost" data-testid="hero-cta-help">
                                Non riesco ad accedere
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5 flex justify-center lg:justify-end fade-up delay-2">
                        <img
                            src={LOGO_10_ANNI}
                            alt="StartupItalia — 10 anni"
                            className="w-full max-w-md drop-shadow-[0_10px_40px_rgba(236,72,153,0.25)]"
                            data-testid="hero-image"
                        />
                    </div>
                </div>
            </section>

            {/* CHI SIAMO */}
            <section id="chi-siamo" className="py-24 lg:py-32 border-t border-zinc-900">
                <div className="max-w-3xl mx-auto px-6 lg:px-12">
                    <p
                        className="font-display text-3xl lg:text-4xl leading-[1.15] tracking-tight text-zinc-100"
                        data-testid="mission-text"
                    >
                        Usiamo <span className="font-serif-italic text-acid">dati</span>, tecnologia e creatività per
                        parlare al cuore delle persone, informarle, formarle e intrattenerle.
                    </p>
                    <p className="mt-8 text-zinc-400 leading-relaxed">
                        Dal 2013 siamo il punto di riferimento dell&apos;ecosistema dell&apos;innovazione e dal 2015 i creatori
                        del primo e più importante evento del settore in Italia. La nostra esperienza decennale ci
                        rende un partner abilitatore nel costruire community, sviluppare progetti, implementare
                        tecnologie e semplificare processi per sostenere la crescita di un ecosistema capace di dare
                        vita a nuove imprese e generare la creazione di nuovi posti di lavoro.
                    </p>
                </div>
            </section>

            {/* STORIA */}
            <section id="storia" className="py-24 lg:py-32 border-t border-zinc-900 bg-[#080808]">
                <div className="max-w-3xl mx-auto px-6 lg:px-12">
                    <span className="label-tag">— La nostra storia</span>
                    <h2 className="font-display text-4xl lg:text-5xl mt-4 tracking-tighter leading-[1.05]">
                        Dieci anni di{" "}
                        <span className="font-serif-italic text-gradient-tropical">passione</span>, impegno e
                        innovazione al servizio del vostro domani.
                    </h2>

                    <div className="mt-12 space-y-8" data-testid="story-timeline">
                        <TimelineRow year="2013">
                            StartupItalia nasce con l&apos;obiettivo di promuovere la cultura dell&apos;innovazione in tutto
                            l&apos;ecosistema italiano.
                        </TimelineRow>
                        <TimelineRow year="2015">
                            Creiamo StartupItalia Open Summit, il primo evento in Italia sul mondo dell&apos;innovazione
                            che riunisce tutti i protagonisti del settore.
                        </TimelineRow>
                        <TimelineRow year="2018 – 2021">
                            Una campagna di equity crowdfunding e un successivo aumento di capitale portano oltre 2000
                            persone a credere nel progetto.
                        </TimelineRow>
                        <TimelineRow year="Oggi">
                            Un gruppo solido di talenti che operano su tre sedi — Milano e Cagliari — dedicati
                            a costruire l&apos;ecosistema dell&apos;innovazione italiana.
                        </TimelineRow>
                       
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="border-t border-zinc-900 py-10" data-testid="landing-footer">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 space-y-4">
                    <p
                        className="text-xs font-grotesk text-zinc-500 leading-relaxed max-w-3xl"
                        data-testid="footer-legal-text"
                    >
                        StartupItalia è una testata registrata presso il Tribunale di Roma n. 167/2012.
                        Editore: Pull The Rabbit S.r.l.
                    </p>
                    <div className="flex flex-wrap justify-between gap-4 text-xs font-grotesk text-zinc-500 uppercase tracking-widest">
                        <div>© 2026 Pull The Rabbit S.r.l. Cagliari, Via Sassari 3, 09123, PIVA 13733231008, REA  CA  352408, Capitale Sociale: €26.422,79 - PEC startupitalia(at)legalmail.it</div>
                        
                        <div className="flex gap-6">
                            <a
                                href="https://startupitalia.eu/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-acid"
                                data-testid="footer-privacy-link"
                            >
                                Privacy
                            </a>
                            <a
                                href="https://startupitalia.eu/cookie-policy/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-acid"
                                data-testid="footer-cookie-link"
                            >
                                Cookie
                            </a>
                            <a
                                className="clickio-cmp-settings-text clickio-cmp-settings-display hover:text-acid"
                                style={{ display: "none" }}
                                href="#"
                                data-testid="footer-cookie-settings-link"
                            >
                                Gestione cookie
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function TimelineRow({ year, children }) {
    return (
        <div className="grid grid-cols-[110px_1fr] gap-6 border-t border-zinc-900 pt-6" data-testid={`timeline-${year.replace(/[^\w]/g, "-")}`}>
            <div className="font-display text-magenta text-xl tracking-tighter">{year}</div>
            <p className="text-zinc-300 leading-relaxed">{children}</p>
        </div>
    );
}
